import { styled } from "goober";
import { useState } from "react";
import { formatTinesUnits } from "../../lib/formatTinesUnits";
import { monthlyTinesUnitsLimit } from "../../lib/session";
import { useMakeUrl, routes } from "../../lib/urls";
import { TinesUnitsBreakdown } from "./TinesUnitsBreakdown";
import { HelpTooltip } from "../HelpTooltip";
import { isInAppNavigation, Link } from "../Link";
import { Text } from "../Text";
import {
  CursorTooltip,
  TooltipLabel,
  TooltipRow,
  TooltipValue,
} from "../CursorTooltip";
import { useMonitoringInteraction } from "./analytics";
import { USAGE_FILL, usageStatus } from "./tinesUnitsStatus";
import { useMonitoringWorkflows } from "./useMonitoringWorkflows";
import { Panel, PanelHeader, PanelSkeleton, TotalRow } from "./workflowsLayout";

const GaugeContainer = styled("div")`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 900px) {
    flex: none;
    min-height: 32px;
  }
`;

const GaugeLink = styled(Link)`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  text-decoration: none;

  @media (max-width: 900px) {
    flex: none;
    min-height: 32px;
  }
`;

const GaugeBar = styled("div")`
  width: 6px;
  height: 100%;
  border-radius: 999px;
  background: var(--alpha8);
  display: flex;
  flex-direction: column-reverse;
  overflow: hidden;
  transition:
    width var(--motion-fast) var(--ease-out),
    height var(--motion-fast) var(--ease-out);

  &:hover {
    width: 9px;
  }

  @media (max-width: 900px) {
    width: 100%;
    height: 6px;
    flex-direction: row;

    &:hover {
      width: 100%;
      height: 9px;
    }
  }
`;

const GaugeFill = styled("div")`
  flex-basis: 0;
  min-width: 0;
  min-height: 0;
  border-radius: 999px;
  z-index: 1;
`;

const GaugeSegment = styled("div")`
  flex-basis: 0;
  min-width: 0;
  min-height: 0;
`;

// Tucks under the fill's rounded end so the two meet without a corner.
const GaugeForecast = styled(GaugeSegment)`
  border-radius: 999px;
  margin-bottom: -6px;

  @media (max-width: 900px) {
    margin-bottom: 0;
    margin-left: -6px;
  }
`;

const Rail = styled("div")`
  flex: 0 0 120px;
  display: flex;
  flex-direction: column;

  @media (max-width: 900px) {
    flex: none;
  }
`;

const RailPanel = styled(Panel)`
  flex: 1;
  padding: 32px 16px;
`;

const TitleRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const BottomLabel = styled("div")`
  text-align: center;
`;

export function TinesUnitsPanel() {
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);
  const makeUrl = useMakeUrl();
  const { report, loading } = useMonitoringWorkflows();
  const recordInteraction = useMonitoringInteraction();

  const limit = monthlyTinesUnitsLimit();
  if (limit === 0) return null;

  if (loading) {
    return (
      <Rail>
        <PanelSkeleton />
      </Rail>
    );
  }

  const units = report.units;
  if (!units) return null;

  const canDrill = report.unitsBySpace != null;
  const recordOpen = () => {
    recordInteraction("workflows.panel_drilldown_open", { value: "units" });
  };
  const breakdownUrl = makeUrl(routes.monitoring.workflows, {
    panel: "units",
    space: "",
  });

  const fraction = units.total / limit;
  const fill = USAGE_FILL[usageStatus(fraction)];
  const percent = Math.round(fraction * 100);
  const forecast = report.unitsTrend?.projectedTotal ?? null;
  // The bar stops at the limit even when the forecast goes past it.
  const forecastGrowth =
    forecast != null ? Math.max(0, Math.min(forecast, limit) - units.total) : 0;
  const headroom = Math.max(0, limit - units.total - forecastGrowth);
  const gauge = (
    <GaugeBar
      onPointerMove={
        forecast != null
          ? (e) => setPointer({ x: e.clientX, y: e.clientY })
          : undefined
      }
      onPointerLeave={() => setPointer(null)}
    >
      <GaugeFill style={{ flexGrow: units.total, background: fill }} />
      {forecastGrowth > 0 && (
        <GaugeForecast
          style={{
            flexGrow: forecastGrowth,
            background: `color-mix(in srgb, ${fill} 30%, transparent)`,
          }}
        />
      )}
      {headroom > 0 && <GaugeSegment style={{ flexGrow: headroom }} />}
    </GaugeBar>
  );

  return (
    <Rail>
      <RailPanel>
        <PanelHeader style={{ alignItems: "center" }}>
          <TitleRow>
            <Text variant="text-small-md">Tines units</Text>
            <HelpTooltip content="A single measure of your usage this month, shown against your plan limit." />
          </TitleRow>
          <TotalRow>
            <Text variant="display-md">{percent}%</Text>
          </TotalRow>
        </PanelHeader>

        {canDrill ? (
          <GaugeLink
            to={breakdownUrl}
            aria-label="View Tines units breakdown"
            onPointerDown={(event) => {
              if (isInAppNavigation(event)) recordOpen();
            }}
            onClick={(event) => {
              if (event.detail === 0) recordOpen();
            }}
          >
            {gauge}
          </GaugeLink>
        ) : (
          <GaugeContainer>{gauge}</GaugeContainer>
        )}

        <BottomLabel>
          <Text variant="text-small" color="var(--text-secondary)">
            {formatTinesUnits(units.total)} / {formatTinesUnits(limit)}
          </Text>
        </BottomLabel>
        {pointer && forecast != null && (
          <CursorTooltip x={pointer.x} y={pointer.y} contentKey="units">
            <TooltipRow>
              <TooltipLabel>Used so far</TooltipLabel>
              <TooltipValue>{formatTinesUnits(units.total)}</TooltipValue>
            </TooltipRow>
            <TooltipRow>
              <TooltipLabel>By month end</TooltipLabel>
              <TooltipValue>{formatTinesUnits(forecast)}</TooltipValue>
            </TooltipRow>
          </CursorTooltip>
        )}
      </RailPanel>
      {canDrill && <TinesUnitsBreakdown />}
    </Rail>
  );
}
